// Bootstraps the data layer: local database → store → sync engine.

import { applyLaunchParams, devicePrefs, setDevicePrefs } from './device.ts';
import { openLocalDB, type LocalDB } from './idb.ts';
import { Store } from './store.ts';
import { SyncEngine } from './sync.ts';

export interface DataLayer {
  db: LocalDB;
  store: Store;
  sync: SyncEngine;
}

let layer: DataLayer | undefined;

export function data(): DataLayer {
  if (!layer) throw new Error('Data layer not initialised');
  return layer;
}

export async function initData(): Promise<DataLayer> {
  applyLaunchParams();
  const db = await openLocalDB();

  // localStorage and IndexedDB can be cleared independently by kiosk
  // browsers; keep a copy of the connection settings in both.
  const backup = await db.getMeta<{ key?: string; serverUrl?: string; deviceId?: string }>('device');
  const prefs = devicePrefs();
  if (!prefs.key && backup?.key) setDevicePrefs({ key: backup.key, serverUrl: backup.serverUrl ?? '' });
  if (backup?.deviceId && backup.deviceId !== prefs.deviceId && !prefs.personId) setDevicePrefs({ deviceId: backup.deviceId });
  const p = devicePrefs();
  await db.setMeta('device', { key: p.key, serverUrl: p.serverUrl, deviceId: p.deviceId });

  const store = new Store(db, p.deviceId);
  await store.load();
  const sync = new SyncEngine(store, db, { serverUrl: p.serverUrl, key: p.key });
  layer = { db, store, sync };
  void sync.start();
  return layer;
}

/** Re-point the sync engine after the user changes server settings. */
export async function reconnect(): Promise<void> {
  const l = data();
  l.sync.stop();
  const p = devicePrefs();
  await l.db.setMeta('device', { key: p.key, serverUrl: p.serverUrl, deviceId: p.deviceId });
  l.sync = new SyncEngine(l.store, l.db, { serverUrl: p.serverUrl, key: p.key });
  syncListeners.forEach((fn) => fn());
  await l.sync.start();
}

const syncListeners = new Set<() => void>();
export function onSyncEngineChange(fn: () => void): () => void {
  syncListeners.add(fn);
  return () => syncListeners.delete(fn);
}

/** Tests: run the data layer on an in-memory store (no sync). */
export function setDataForTests(store: Store, db: LocalDB): void {
  layer = { db, store, sync: { stop() {}, start: async () => {}, syncNow: async () => {} } as unknown as SyncEngine };
}
